import { useEffect, useCallback } from 'react'
import { useAuth } from '@/contexts/AuthContext'

type Theme = 'light' | 'dark' | 'system'

export function useTheme() {
    const { user, updatePreferences } = useAuth()
    // Use localStorage as fallback while user data loads (prevents anti-FOUC class removal)
    const theme: Theme = user?.preferences?.global?.theme
        ?? (localStorage.getItem('argalis-theme') as Theme | null)
        ?? 'light'

    useEffect(() => {
        const applyTheme = () => {
            const isDark =
                theme === 'dark' ||
                (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
            document.documentElement.classList.toggle('dark', isDark)
            localStorage.setItem('argalis-theme', theme)
        }

        applyTheme()

        // Listen for system changes when in 'system' mode
        if (theme === 'system') {
            const mq = window.matchMedia('(prefers-color-scheme: dark)')
            mq.addEventListener('change', applyTheme)
            return () => mq.removeEventListener('change', applyTheme)
        }
    }, [theme])

    const setTheme = useCallback(
        (newTheme: Theme) => {
            updatePreferences({ global: { theme: newTheme } })
        },
        [updatePreferences],
    )

    return { theme, setTheme }
}
